// The boot step runner: steps run one after another in the order given (the game lists them in dependency order),
// each with a real duration and a staged caption. Progress moves only when a step reports finished work units
// (`done(n)`) or finishes, never on a timer, so the bar can't run ahead of the work. A step that takes longer than its
// timeout rejects with a BootTimeoutError instead of hanging: a stalled WASM init surfaces as an error the fail screen
// and the harness can see.
//
// Research: engine/docs/research/suggestions/performance-build-assets.md (boot shell: staged captions, real progress).

/** What a running step can do. */
export interface BootStepContext {
  /** Report `n` more finished work units of this step (default 1); capped at the step's `units`. */
  done(n?: number): void;
  /** Replace the caption shown for this step (e.g. "Building meshes 3/12"). */
  caption(text: string): void;
  /** Wait for the next frame, so captions and the bar paint between chunks of work. */
  frame(): Promise<void>;
  /** Aborted when the step times out, so long work can stop early. */
  signal: AbortSignal;
  /** Results of the steps that already finished, by id. */
  results: Readonly<Record<string, unknown>>;
}

/** One boot step. */
export interface BootStep {
  /** Unique id, used in the report and in `results`. */
  id: string;
  /** The staged caption shown while the step runs (a player-facing string: the game passes it in). */
  caption: string;
  /** Work units the step reports through `done()`. Default 1 (the step's end is its one unit). */
  units?: number;
  /** Milliseconds before the step fails with a BootTimeoutError. Default: the runner's `timeoutMs`. */
  timeoutMs?: number;
  /** Milliseconds over which a finished step is marked `warn` instead of `ok`. Default: no warning. */
  warnMs?: number;
  /** The work. Its resolved value lands in `results[id]`. */
  run(c: BootStepContext): unknown;
}

/** The status of one step. */
export type StepStatus = "pending" | "running" | "ok" | "warn" | "failed" | "timeout";

/** One step's record, for the boot report and the step log. */
export interface StepRecord {
  id: string;
  caption: string;
  units: number;
  /** Units reported so far. */
  done: number;
  status: StepStatus;
  /** When the step started, on the runner's clock (ms). */
  startMs: number;
  /** How long it took (ms); while running, so far. */
  ms: number;
  /** The error message, when it failed or timed out. */
  error?: string;
}

/** A progress event: emitted only when work finishes. */
export interface BootProgress {
  /** Finished units across all steps. */
  done: number;
  /** All units across all steps. */
  total: number;
  /** done / total, 0..1. */
  fraction: number;
  /** The step that reported the work. */
  step: string;
  /** The runner's clock when it was reported (ms). */
  t: number;
}

/** Thrown when a step outlives its timeout. */
export class BootTimeoutError extends Error {
  constructor(
    readonly step: string,
    readonly timeoutMs: number,
  ) {
    super(`boot step "${step}" did not finish in ${timeoutMs} ms`);
    this.name = "BootTimeoutError";
  }
}

/** Thrown by runBoot when a step fails: names the step and keeps the original error as `cause`. */
export class BootStepError extends Error {
  constructor(
    readonly step: string,
    override readonly cause: unknown,
  ) {
    super(cause instanceof BootTimeoutError ? cause.message : `boot step "${step}" failed: ${cause instanceof Error ? cause.message : String(cause)}`);
    this.name = cause instanceof BootTimeoutError ? "BootTimeoutError" : "BootStepError";
    if (cause instanceof Error && cause.stack) this.stack = `${this.name}: ${this.message}\n${cause.stack}`;
  }
}

/** Options for runBoot. */
export interface BootRunOptions {
  /** Called on every finished unit. */
  onProgress?(p: BootProgress): void;
  /** Called when a step starts and when it ends (the same record object, updated). */
  onStep?(r: StepRecord): void;
  /** The clock, ms. Default `performance.now`. */
  now?(): number;
  /** Wait one frame. Default requestAnimationFrame where it exists, else a macrotask. */
  frame?(): Promise<void>;
  /** Default step timeout, ms. Default 30 000. */
  timeoutMs?: number;
  /** Timer functions (tests pass fakes). */
  setTimeout?(fn: () => void, ms: number): unknown;
  clearTimeout?(id: unknown): void;
}

/** The outcome of a boot run. */
export interface BootResult {
  /** Each step's resolved value, by id. */
  results: Record<string, unknown>;
  /** Every step's record, in order. */
  steps: StepRecord[];
  /** Total wall time, ms. */
  ms: number;
}

const defaultFrame = (): Promise<void> =>
  new Promise((r) => (typeof requestAnimationFrame === "function" ? requestAnimationFrame(() => r()) : setTimeout(r, 0)));

/** A boot run's live view (the records fill in as it goes) and its promise. */
export interface BootRun {
  /** Every step's record, in order (pending ones included). */
  steps: StepRecord[];
  /** Every progress event so far. */
  progress: BootProgress[];
  /** Resolves when every step has finished; rejects with a BootStepError on the first failure. */
  done: Promise<BootResult>;
}

/**
 * Run boot steps in order. Each step's duration is measured; progress events fire only as units finish; a step that
 * throws, rejects or times out stops the run with a BootStepError naming it. Yields a frame before each step so the
 * caption paints.
 */
export function runBoot(steps: readonly BootStep[], o: BootRunOptions = {}): BootRun {
  const ids = new Set<string>();
  for (const s of steps) {
    if (ids.has(s.id)) throw new Error(`duplicate boot step id "${s.id}"`);
    ids.add(s.id);
    if (s.units !== undefined && !(Number.isInteger(s.units) && s.units >= 1)) throw new Error(`boot step "${s.id}": units must be a whole number ≥ 1`);
  }
  const now = o.now ?? (() => performance.now());
  const frame = o.frame ?? defaultFrame;
  const setT = o.setTimeout ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
  const clearT = o.clearTimeout ?? ((id: unknown) => clearTimeout(id as ReturnType<typeof setTimeout>));
  const total = steps.reduce((s, x) => s + (x.units ?? 1), 0);
  const records: StepRecord[] = steps.map((s) => ({ id: s.id, caption: s.caption, units: s.units ?? 1, done: 0, status: "pending", startMs: 0, ms: 0 }));
  const progress: BootProgress[] = [];
  const results: Record<string, unknown> = {};
  let finished = 0;

  const report = (rec: StepRecord, n: number): void => {
    const add = Math.min(n, rec.units - rec.done);
    if (!(add > 0)) return;
    rec.done += add;
    finished += add;
    const p: BootProgress = { done: finished, total, fraction: total ? finished / total : 1, step: rec.id, t: now() };
    progress.push(p);
    o.onProgress?.(p);
  };

  const done = (async (): Promise<BootResult> => {
    const t0 = now();
    for (let i = 0; i < steps.length; i++) {
      const step = steps[i]!;
      const rec = records[i]!;
      await frame();
      const ac = new AbortController();
      rec.status = "running";
      rec.startMs = now();
      o.onStep?.(rec);
      const timeoutMs = step.timeoutMs ?? o.timeoutMs ?? 30_000;
      let timer: unknown;
      const ctx: BootStepContext = {
        done: (n = 1) => {
          if (rec.status === "running") report(rec, n);
        },
        caption: (text) => {
          rec.caption = text;
          o.onStep?.(rec);
        },
        frame,
        signal: ac.signal,
        results,
      };
      try {
        const timeout = new Promise<never>((_, reject) => {
          timer = setT(() => {
            ac.abort();
            reject(new BootTimeoutError(step.id, timeoutMs));
          }, timeoutMs);
        });
        results[step.id] = await Promise.race([Promise.resolve().then(() => step.run(ctx)), timeout]);
      } catch (e) {
        rec.ms = now() - rec.startMs;
        rec.status = e instanceof BootTimeoutError ? "timeout" : "failed";
        rec.error = e instanceof Error ? e.message : String(e);
        o.onStep?.(rec);
        throw new BootStepError(step.id, e);
      } finally {
        clearT(timer);
      }
      rec.ms = now() - rec.startMs;
      report(rec, rec.units - rec.done); // the step's end finishes whatever it didn't report
      rec.status = step.warnMs !== undefined && rec.ms > step.warnMs ? "warn" : "ok";
      o.onStep?.(rec);
    }
    return { results, steps: records, ms: now() - t0 };
  })();
  return { steps: records, progress, done };
}

/** One step as a boot-log line: `[ OK ] rapier   Loading physics   112 ms`. */
export function formatStepLine(r: StepRecord): string {
  const tag = { pending: "[    ]", running: "[ .. ]", ok: "[ OK ]", warn: "[WARN]", failed: "[FAIL]", timeout: "[TIME]" }[r.status];
  const ms = r.status === "pending" ? "" : ` ${Math.round(r.ms)} ms`;
  return `${tag} ${r.id.padEnd(10)} ${r.caption}${ms}`;
}

/**
 * The check a test runs on a progress bar: every value the bar showed must be a finished-work fraction already
 * reported by then (or 0), every reported fraction must reach the bar, and the last value must be 1 when the run
 * finished. Returns the problems found (empty: the bar was driven by finished work, never a timer).
 */
export function checkProgressTrace(shown: readonly { value: number; t: number }[], progress: readonly BootProgress[], finished: boolean): string[] {
  const problems: string[] = [];
  const eps = 1e-6;
  for (const s of shown) {
    // fine: 0, or a fraction some step had already reported by then (a bar may lag the work, never lead or invent it)
    const backed = Math.abs(s.value) < eps || progress.some((p) => p.t <= s.t + eps && Math.abs(p.fraction - s.value) < eps);
    if (!backed) {
      let f = 0;
      for (const p of progress) if (p.t <= s.t + eps) f = p.fraction;
      problems.push(`bar showed ${s.value.toFixed(3)} at ${s.t.toFixed(1)} ms when finished work was ${f.toFixed(3)}`);
    }
  }
  // every finished unit must reach the bar: a bar that skips the work (a timer, a jump to the end) shows too few
  for (const p of progress) {
    if (!shown.some((s) => s.t + eps >= p.t && Math.abs(s.value - p.fraction) < eps)) problems.push(`bar never showed ${p.fraction.toFixed(3)} (step ${p.step} finished work at ${p.t.toFixed(1)} ms)`);
  }
  if (finished) {
    const last = shown[shown.length - 1];
    if (!last || Math.abs(last.value - 1) > eps) problems.push(`bar ended at ${last ? last.value.toFixed(3) : "nothing"}, not 1`);
  }
  if (progress.length && !shown.length) problems.push("bar never moved");
  return problems;
}
